import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";
import { ReactComponent as ServicesIcon } from "../../../assets/icons/sidebar-services.svg";
import { ReactComponent as ProfileManagementIcon } from "../../../assets/icons/sidebar-profile-management.svg";
import { ReactComponent as IpCategoryIcon } from "../../../assets/icons/sidebar-ip-category.svg";
import { ReactComponent as ProcedureManagementIcon } from "../../../assets/icons/sidebar-procedure-management.svg";
import { ReactComponent as DocumentsIcon } from "../../../assets/icons/sidebar-documents.svg";
import { ReactComponent as AccountManagementIcon } from "../../../assets/icons/sidebar-account-management.svg";
import { ReactComponent as ApprovalManagementIcon } from "../../../assets/icons/sidebar-approval-management.svg";
import { ReactComponent as OrderManagementIcon } from "../../../assets/icons/sidebar-order-management.svg";
import { ReactComponent as IpMarketplaceIcon } from "../../../assets/icons/sidebar-ip-marketplace.svg";
import { ReactComponent as StatisticsIcon } from "../../../assets/icons/sidebar-statistics.svg";
import { ReactComponent as PolicyIcon } from "../../../assets/icons/sidebar-policy.svg";
import { ReactComponent as QuotationIcon } from "../../../assets/icons/sidebar-quotation.svg";

const getSidebarItemClass = (isActive, extra = "") =>
  [
    "group flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-sf-medium leading-5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0155F8]/25 focus-visible:ring-offset-2",
    isActive
      ? "bg-[#F1F6FF] font-sf-semibold text-[#0155F8]"
      : "text-[#30394A] hover:bg-[#F6FAFF] hover:text-[#0155F8]",
    extra,
  ]
    .filter(Boolean)
    .join(" ");

const getSidebarSubItemClass = (isActive) =>
  [
    "block truncate rounded-lg px-3 py-2 text-sm font-sf-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0155F8]/25 focus-visible:ring-offset-2",
    isActive
      ? "bg-[#F1F6FF] font-sf-semibold text-[#0155F8]"
      : "text-[#3F4A5F] hover:bg-[#F6FAFF] hover:text-[#0155F8]",
  ].join(" ");

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const { user_client } = useContext(AuthContext);
  const [openMenus, setOpenMenus] = useState({});
  const [floatingSubmenu, setFloatingSubmenu] = useState(null);
  const submenuRefs = useRef({});

  const checkIsActive = useCallback(
    (path) => {
      const params = new URLSearchParams(location.search);
      const isAssetProfileDetail =
        params.get("from") === "asset" &&
        location.pathname.startsWith("/quan-ly-ho-so/ho-so/");

      if (isAssetProfileDetail) {
        return path === "/danh-muc-tai-san-tri-tue";
      }

      return (
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
    },
    [location.pathname, location.search],
  );

  const navLinks = useMemo(
    () => [
      {
        label: "Danh sách dịch vụ",
        to: "/danh-sach-dich-vu",
        icon: <ServicesIcon />,
      },
      {
        label: "Quản lý hồ sơ",
        to: "/quan-ly-ho-so",
        icon: <ProfileManagementIcon />,
      },
      {
        label: "Danh mục tài sản trí tuệ",
        to: "/danh-muc-tai-san-tri-tue",
        icon: <IpCategoryIcon />,
      },
      {
        label: "Hướng dẫn thủ tục",
        to: "/huong-dan-thu-tuc",
        icon: <ProcedureManagementIcon />,
      },
      {
        label: "Tài liệu",
        to: "/tai-lieu",
        icon: <DocumentsIcon />,
      },
      {
        label: "Quản lý tài khoản",
        to: "#",
        icon: <AccountManagementIcon />,
        subLinks: [
          { label: "Tài khoản của tôi", to: "/tai-khoan-cua-toi" },
          ...(user_client.role === "Manager"
            ? [{ label: "Tài khoản nhân viên", to: "/tai-khoan-nhan-vien" }]
            : []),
        ],
      },
      {
        label: "Danh sách yêu cầu",
        to: "/danh-sach-yeu-cau",
        icon: <ApprovalManagementIcon />,
      },
      {
        label: "Hợp đồng, chứng từ",
        to: "/danh-sach-hop-dong",
        icon: <OrderManagementIcon />,
      },
      {
        label: "Mua bán sở hữu trí tuệ",
        to: "/mua-ban-so-huu-tri-tue",
        icon: <IpMarketplaceIcon />,
      },
      {
        label: "Chính sách",
        to: "#",
        icon: <PolicyIcon />,
        subLinks: [
          { label: "Quy định", to: "/quy-dinh-su-dung" },
          { label: "Chính sách bảo mật", to: "/chinh-sach-bao-mat" },
          { label: "Chính sách hoàn tiền", to: "/chinh-sach-hoan-tien" },
          { label: "Chính sách cập nhật", to: "/chinh-sach-cap-nhat" },
        ],
      },
      {
        label: "Báo phí",
        to: "/bao-phi",
        icon: <QuotationIcon />,
      },
      {
        label: "Báo cáo thống kê",
        to: "#",
        icon: <StatisticsIcon />,
        subLinks: [
          { label: "Tổng quan", to: "/bao-cao-thong-ke/tong-quan" },
          { label: "Báo cáo dịch vụ", to: "/bao-cao-thong-ke/dich-vu" },
          { label: "Báo cáo hồ sơ", to: "/bao-cao-thong-ke/ho-so" },
          { label: "Báo cáo tài chính", to: "/bao-cao-thong-ke/tai-chinh" },
        ],
      },
      {
        label: "Lịch sử thanh toán",
        to: "/lich-su-thanh-toan",
        icon: <OrderManagementIcon />,
      },
    ],
    [user_client.role],
  );

  useEffect(() => {
    const newOpen = {};
    navLinks.forEach((link, i) => {
      if (link.subLinks?.some((sub) => checkIsActive(sub.to)))
        newOpen[i] = true;
    });
    setOpenMenus(newOpen);
  }, [location.pathname, navLinks, checkIsActive]);

  const toggleMenu = (index) => {
    setOpenMenus((prev) => {
      const next = { ...prev, [index]: !prev[index] };
      if (!isOpen && !prev[index]) {
        const rect = submenuRefs.current[index]?.getBoundingClientRect();
        if (rect) {
          setFloatingSubmenu({
            top: rect.top,
            left: rect.right + 8,
            items: navLinks[index].subLinks,
            activeTo: location.pathname,
          });
        }
      } else {
        setFloatingSubmenu(null);
      }
      return next;
    });
  };

  return (
    <>
      <aside
        className={`${
          isOpen ? "w-[280px]" : "w-[86px]"
        } h-full overflow-hidden shrink-0 transition-[width] duration-300 ease-in-out`}
        style={{ willChange: "width" }}
      >
        <div className="flex h-full w-full flex-col justify-between bg-white p-4 border-r">
          <nav className="h-full overflow-y-auto py-4">
            <ul className="space-y-1">
              {navLinks.map((link, index) => {
                const hasActiveSubLink = link.subLinks?.some((sub) =>
                  checkIsActive(sub.to),
                );
                const isActive = checkIsActive(link.to) || hasActiveSubLink;
                const isSubOpen = openMenus[index];

                const ItemLeft = (
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex size-7 shrink-0 items-center justify-center text-current">
                      {link.icon}
                    </span>
                    <span
                      className={[
                        "truncate transition-[max-width,opacity] duration-300 ease-in-out",
                        isOpen
                          ? "max-w-[180px] opacity-100"
                          : "max-w-0 opacity-0",
                      ].join(" ")}
                      title={link.label}
                    >
                      {link.label}
                    </span>
                  </div>
                );

                if (link.subLinks) {
                  return (
                    <li key={index} className="relative">
                      <button
                        ref={(el) => (submenuRefs.current[index] = el)}
                        onClick={() => toggleMenu(index)}
                        className={getSidebarItemClass(
                          isActive,
                          "justify-between",
                        )}
                        aria-expanded={isSubOpen}
                      >
                        {ItemLeft}

                        <div
                          className={[
                            "ml-3 h-4 flex-shrink-0 overflow-hidden",
                            "transition-[width,opacity] ease-linear",
                            isOpen
                              ? "w-4 opacity-100 delay-[100ms] duration-0"
                              : "w-0 opacity-0 delay-[0ms] duration-0 pointer-events-none",
                          ].join(" ")}
                          aria-hidden={!isOpen}
                          style={{ willChange: "width, opacity" }}
                        >
                          <ArrowDownIcon
                            className={[
                              "h-4 w-4",
                              "transition-transform duration-300 ease-linear",
                              isSubOpen ? "rotate-180" : "rotate-0",
                            ].join(" ")}
                            style={{ willChange: "transform" }}
                          />
                        </div>
                      </button>

                      <div
                        className={[
                          "pl-8",
                          isOpen
                            ? isSubOpen
                              ? "grid grid-rows-[1fr] mt-1"
                              : "grid grid-rows-[0fr] mt-0"
                            : "hidden",
                          "transition-[grid-template-rows,margin] duration-300 ease-in-out",
                        ].join(" ")}
                      >
                        <ul className="space-y-1 overflow-hidden">
                          {link.subLinks.map((sub, idx) => (
                            <li key={idx}>
                              <Link
                                to={sub.to}
                                className={getSidebarSubItemClass(
                                  location.pathname === sub.to,
                                )}
                                title={sub.label}
                              >
                                {sub.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={index}>
                    {link.external ? (
                      <a
                        href={link.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={getSidebarItemClass(false)}
                        title={link.label}
                      >
                        {ItemLeft}
                      </a>
                    ) : (
                      <Link
                        to={link.to}
                        className={getSidebarItemClass(isActive)}
                        title={link.label}
                      >
                        {ItemLeft}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          <button
            className="mt-4 flex min-h-[44px] items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-sf-medium text-[#30394A] transition-colors duration-150 hover:bg-[#F6FAFF] hover:text-[#0155F8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0155F8]/25 focus-visible:ring-offset-2"
            onClick={toggleSidebar}
            title={isOpen ? "Thu gọn" : "Mở rộng"}
          >
            <img
              src={isOpen ? "/icons/close-menu.svg" : "/icons/open-menu.svg"}
              alt="toggle"
              className="size-3.5 shrink-0"
            />
            <span
              className={[
                "truncate transition-[max-width,opacity] duration-300 ease-in-out",
                isOpen ? "max-w-[120px] opacity-100" : "max-w-0 opacity-0",
              ].join(" ")}
            >
              Thu gọn
            </span>
          </button>
        </div>
      </aside>

      {!isOpen && floatingSubmenu && (
        <ul
          className="fixed z-[9999] min-w-[200px] rounded-xl border border-slate-100 bg-white p-2 shadow-[0_16px_40px_rgba(15,23,42,0.14)]"
          style={{ top: floatingSubmenu.top, left: floatingSubmenu.left }}
        >
          {floatingSubmenu.items.map((sub, idx) => (
            <li key={idx}>
              <Link
                to={sub.to}
                className={getSidebarSubItemClass(
                  floatingSubmenu.activeTo === sub.to,
                )}
                title={sub.label}
              >
                {sub.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default Sidebar;
